// BOORO: the wardrobe for ghost avatars, shown on the Ghost tab of Avatar Studio.
import type { GhostSpec } from "@booro/ghost-avatar";
import {
  encodeGhostAvatar,
  GHOST_BODIES,
  GHOST_CLOTH_COLORS,
  GHOST_FACES,
  GHOST_HANDS,
  GHOST_HEADS,
  GHOST_MOUTHS,
  GHOST_NECKS,
  randomGhostSpec,
} from "@booro/ghost-avatar";
import { useLingui } from "@lingui/react/macro";
import {
  BOT_AVATAR_COLORS,
  GhostAvatar,
  parseBotAvatar,
  resolveBotGhost,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@rakazo/ui-web";
import { Dices } from "lucide-react";
import { useState } from "react";

type Slot = "body" | "head" | "face" | "neck" | "hand";

const COLORED_HEADS = new Set<GhostSpec["head"]>(["cap", "beanie", "tophat", "bow", "crown"]);

export function GhostWardrobe({
  value,
  identity,
  onChange,
}: {
  value: string;
  identity?: string;
  onChange: (value: string) => void;
}) {
  const { t } = useLingui();
  const [slot, setSlot] = useState<Slot>("body");
  const parsed = parseBotAvatar(value, identity);
  const ghost = resolveBotGhost(value, identity || parsed.color || "agent", parsed.shapeIndex);
  const current: GhostSpec = { ...ghost.spec, color: ghost.spec.color ?? ghost.colorDef.hex };

  function dress(patch: Partial<GhostSpec>) {
    onChange(encodeGhostAvatar({ ...current, ...patch }));
  }

  function selectColor(color: string) {
    // An undressed bot keeps its random outfit; only the color is stored.
    if (value.startsWith("data:image/") || /^#[0-9a-fA-F]{3,6}$/.test(value)) onChange(color);
    else dress({ color });
  }

  function shuffle() {
    const seed = Math.floor(Math.random() * 2 ** 32);
    dress({ ...randomGhostSpec(seed), color: current.color });
  }

  const labels: Record<string, string> = {
    classic: t`Classic`,
    chubby: t`Chubby`,
    tall: t`Tall`,
    wisp: t`Wisp`,
    bell: t`Bell`,
    bean: t`Bean`,
    cap: t`Cap`,
    beanie: t`Beanie`,
    tophat: t`Top hat`,
    headset: t`Headset`,
    bow: t`Bow`,
    sprout: t`Sprout`,
    crown: t`Crown`,
    round: t`Round glasses`,
    nerd: t`Horn-rimmed glasses`,
    shades: t`Sunglasses`,
    monocle: t`Monocle`,
    tie: t`Tie`,
    bowtie: t`Bow tie`,
    suit: t`Suit`,
    scarf: t`Scarf`,
    lanyard: t`Name badge`,
    coffee: t`Coffee`,
    clipboard: t`Clipboard`,
    tablet: t`Tablet`,
    laptop: t`Laptop`,
    none: t`No mouth`,
    smile: t`Smile`,
    grin: t`Grin`,
  };

  const options: { key: string; label: string; patch: Partial<GhostSpec>; selected: boolean }[] =
    slot === "body"
      ? GHOST_BODIES.map((body) => ({
          key: body,
          label: labels[body] ?? body,
          patch: { body },
          selected: current.body === body,
        }))
      : [
          {
            key: "none",
            label: t`Nothing`,
            patch: { [slot]: null },
            selected: current[slot] === null,
          },
          ...(
            {
              head: GHOST_HEADS,
              face: GHOST_FACES,
              neck: GHOST_NECKS,
              hand: GHOST_HANDS,
            } as const
          )[slot].map((item) => ({
            key: item,
            label: labels[item] ?? item,
            patch: { [slot]: item },
            selected: current[slot] === item,
          })),
        ];

  const showCloth = COLORED_HEADS.has(current.head) || current.neck !== null;

  return (
    <div className="space-y-4 pt-1" data-testid="avatar-studio-bot-tab">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
            {t`Color`}
          </div>
          <button
            type="button"
            onClick={shuffle}
            data-testid="ghost-wardrobe-shuffle"
            className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Dices size={14} />
            {t`Shuffle`}
          </button>
        </div>
        <div className="grid grid-cols-6 place-items-center gap-2">
          {BOT_AVATAR_COLORS.map((color) => {
            const selected = current.color?.toLowerCase() === color.toLowerCase();
            return (
              <button
                key={color}
                type="button"
                onClick={() => selectColor(color)}
                aria-label={t`Color ${color}`}
                aria-pressed={selected}
                className={`size-6 rounded-full border transition-transform hover:scale-110 active:scale-95 focus-visible:ring-2 focus-visible:ring-ring ${
                  selected
                    ? "scale-105 border-transparent ring-2 ring-foreground ring-offset-2 ring-offset-popover"
                    : "border-border"
                }`}
                style={{ backgroundColor: color }}
              />
            );
          })}
        </div>
      </div>

      <div className="border-t border-border pt-3">
        <Tabs value={slot} onValueChange={(next) => setSlot(next as Slot)}>
          <TabsList aria-label={t`Wardrobe`} className="w-full">
            <TabsTrigger value="body">{t`Body`}</TabsTrigger>
            <TabsTrigger value="head">{t`Head`}</TabsTrigger>
            <TabsTrigger value="face">{t`Face`}</TabsTrigger>
            <TabsTrigger value="neck">{t`Neck`}</TabsTrigger>
            <TabsTrigger value="hand">{t`Hands`}</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="mt-3 grid grid-cols-4 gap-2" data-testid={`ghost-wardrobe-${slot}`}>
          {options.map((option) => (
            <WardrobeTile
              key={option.key}
              label={option.label}
              selected={option.selected}
              onClick={() => dress(option.patch)}
              spec={{ ...current, ...option.patch }}
              ghost={ghost}
            />
          ))}
        </div>
        {slot === "face" ? (
          <div className="mt-2 grid grid-cols-4 gap-2" data-testid="ghost-wardrobe-mouth">
            {GHOST_MOUTHS.map((mouth) => (
              <WardrobeTile
                key={mouth}
                label={labels[mouth] ?? mouth}
                selected={current.mouth === mouth}
                onClick={() => dress({ mouth })}
                spec={{ ...current, mouth }}
                ghost={ghost}
              />
            ))}
          </div>
        ) : null}
      </div>

      {showCloth && slot !== "body" ? (
        <div className="border-t border-border pt-2">
          <div className="mb-2 text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
            {t`Outfit color`}
          </div>
          <div className="grid grid-cols-8 place-items-center gap-2">
            {GHOST_CLOTH_COLORS.map((color, index) => (
              <button
                key={color}
                type="button"
                onClick={() => dress({ cloth: index })}
                aria-label={t`Outfit color ${color}`}
                aria-pressed={current.cloth === index}
                className={`size-6 rounded-full border transition-transform hover:scale-110 active:scale-95 focus-visible:ring-2 focus-visible:ring-ring ${
                  current.cloth === index
                    ? "scale-105 border-transparent ring-2 ring-foreground ring-offset-2 ring-offset-popover"
                    : "border-border"
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function WardrobeTile({
  label,
  selected,
  onClick,
  spec,
  ghost,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  spec: GhostSpec;
  ghost: ReturnType<typeof resolveBotGhost>;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={selected}
      title={label}
      onClick={onClick}
      className={`flex aspect-square items-center justify-center rounded-2xl transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none ${
        selected ? "bg-secondary ring-2 ring-foreground" : "hover:bg-muted"
      }`}
    >
      <GhostAvatar spec={spec} colorDef={ghost.colorDef} seed={ghost.seed} size={56} tier="l" />
    </button>
  );
}
